import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";
import { Switch } from "../ui/switch";
import { TargetLengthControl } from "../TargetLengthControl";
import { api, formatApiError } from "../../lib/api";

const STYLES = [["corporate_navy", "Corporate navy"], ["graphite", "Graphite"], ["bold_accent", "Bold accent"]];

function Row({ label, children, testid }) {
    return (
        <div data-testid={testid} className="space-y-1.5 border-b border-neutral-100 pb-4">
            <div className="text-sm font-medium text-neutral-900">{label}</div>
            {children}
        </div>
    );
}

function VoicePicker({ module, save }) {
    const [opts, setOpts] = useState(null);
    const [showAll, setShowAll] = useState(false);
    useEffect(() => {
        api.get(`/modules/${module.id}/voice-options`).then((r) => setOpts(r.data)).catch(() => setOpts({ voices: [] }));
    }, [module.id]);
    if (!opts) return <div className="text-sm text-neutral-500">Loading voices…</div>;
    const g = opts.avatar_gender;
    const current = module.trainer_voice_id || opts.current_voice_id;
    const list = opts.voices.filter((v) => showAll || !g || v.gender === g || v.voice_id === current);
    const picked = opts.voices.find((v) => v.voice_id === current);
    const mismatch = g && picked && picked.gender && picked.gender !== g;
    return (
        <div className="space-y-2">
            <select data-testid="settings-voice-select" value={current || ""} onChange={(e) => save({ trainer_voice_id: e.target.value })}
                className="w-full rounded-md border border-neutral-200 bg-white px-2 py-1.5 text-sm">
                {!current && <option value="">Matched to the trainer</option>}
                {list.map((v) => <option key={v.voice_id} value={v.voice_id}>{v.name} · {v.gender || "unknown"}</option>)}
            </select>
            <label className="flex items-center gap-2 text-xs text-neutral-600">
                <input type="checkbox" data-testid="settings-voice-show-all" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
                Show all voices{g ? ` (the trainer is ${g})` : ""}
            </label>
            {(mismatch || (showAll && g)) && (
                <div data-testid="settings-voice-gender-warning" className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
                    <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                    {mismatch ? `This ${picked.gender} voice doesn't match the ${g} trainer. We'll keep your choice.`
                        : `Voices that don't match the ${g} trainer may sound out of place.`}
                </div>
            )}
        </div>
    );
}

export function VideoSettingsDrawer({ open, onOpenChange, module, onSaved }) {
    if (!module) return null;
    const save = async (body, msg = "Saved — make the video again to apply it") => {
        try {
            await api.patch(`/modules/${module.id}`, body);
            toast.success(msg, { duration: 6000 });
            onSaved && onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent data-testid="video-settings-drawer" className="w-[420px] sm:max-w-[420px] space-y-5 overflow-y-auto">
                <SheetHeader>
                    <SheetTitle>Video settings</SheetTitle>
                    <SheetDescription>Applies to every scene unless a scene has its own custom prompt. Changes apply the next time you make the video.</SheetDescription>
                </SheetHeader>
                <Row label="Length" testid="settings-length">
                    <TargetLengthControl value={module.target_video_minutes} onChange={(v) => save({ target_video_minutes: v }, "Length saved")} testIdPrefix="settings-target-length" />
                </Row>
                <Row label="Voice" testid="settings-voice">
                    <VoicePicker module={module} save={save} />
                </Row>
                <Row label="Trainer on screen" testid="settings-trainer">
                    <div className="flex items-center justify-between text-xs text-neutral-500">
                        <span>{module.trainer_enabled === false ? "Off: trainer scenes use narration over document visuals" : "On"}</span>
                        <Switch checked={module.trainer_enabled !== false} onCheckedChange={(v) => save({ trainer_enabled: v })} data-testid="settings-trainer-switch" />
                    </div>
                </Row>
                <Row label="Style" testid="settings-style">
                    <select data-testid="settings-style-select" value={module.style_preset || "corporate_navy"} onChange={(e) => save({ style_preset: e.target.value })}
                        className="w-full rounded-md border border-neutral-200 bg-white px-2 py-1.5 text-sm">
                        {STYLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                </Row>
                <Row label="Captions" testid="settings-captions">
                    <div className="flex items-center justify-between text-xs text-neutral-500">
                        <span>{module.captions_burned_in === false ? "Off" : "On (shown at the bottom)"}</span>
                        <Switch checked={module.captions_burned_in !== false} onCheckedChange={(v) => save({ captions_burned_in: v })} data-testid="settings-captions-switch" />
                    </div>
                </Row>
            </SheetContent>
        </Sheet>
    );
}
